import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { claimsApi } from "../services/claims";
import { ordersApi } from "../services/orders";
import type { ClaimUploadOutcome, TalentClaimView } from "../types/claims";
import TrackHistoryUploader from "../components/TrackHistoryUploader";
import { formatTxnAmount } from "../utils/currency";
import { claimTimeLeft } from "../utils/claimTimeLeft";
import { formatLocale, t } from "../i18n";
import { T } from "../i18n/T";
import "./DisputeUploadPage.css";
import "./ClaimRespondPage.css";

// 🚨 "Wasn't played?" — where the talent answers a fan's claim (founder,
// 2026-10-09), reached from the Requests banner, the push and the email
// (/claims/:orderId). The chargeback evidence page's look, reworded: the
// request, the 3-day rule, the countdown, then that night's set history
// (the server checks an export for the song; a screenshot is the talent's
// answer too) or "Refund the fan instead". A claim that is already settled —
// or with the founder because the automatic refund failed — shows one line
// saying so instead.

/** "October 9, 11:42 PM" in the app's language. */
const fmtPlayed = (iso: string | null): string => {
  const d = iso ? new Date(iso) : null;
  if (!d || isNaN(d.getTime())) return "—";
  const loc = formatLocale();
  return `${d.toLocaleDateString(loc, { month: "long", day: "numeric" })}, ${d.toLocaleTimeString(loc, { hour: "numeric", minute: "2-digit" })}`;
};

type Line = ClaimUploadOutcome | "refunded" | "review";

const LINE_TONE: Record<Line, "ok" | "warn" | "info"> = {
  rejected: "ok",
  not_found: "warn",
  refunded: "info",
  review: "info",
};

const ClaimRespondPage: React.FC = () => {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [view, setView] = useState<TalentClaimView | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  // What the last set-history upload on this visit did to the claim.
  const [outcome, setOutcome] = useState<ClaimUploadOutcome | null>(null);
  // Bumped after each upload: a fresh uploader for the next try.
  const [uploads, setUploads] = useState(0);
  const [refunding, setRefunding] = useState(false);
  const [refundFailed, setRefundFailed] = useState(false);
  const [, setMinute] = useState(0);

  const load = useCallback(async () => {
    setLoadFailed(false);
    try {
      setView(await claimsApi.get(id));
    } catch (err: any) {
      // Not this talent's request, or no claim on it (an old link, another
      // account): the Requests page is the source of truth — its banner
      // lists every claim still waiting on them.
      if ([400, 403, 404].includes(err?.response?.status)) navigate("/requests", { replace: true });
      else setLoadFailed(true);
    }
  }, [id, navigate]);

  useEffect(() => {
    void load();
  }, [load]);

  // The countdown reads in minutes at its finest — redraw once a minute.
  useEffect(() => {
    const tick = window.setInterval(() => setMinute((n) => n + 1), 60_000);
    return () => window.clearInterval(tick);
  }, []);

  // The single-order upload answers what the file did to the claim.
  const upload = async (fd: FormData) => {
    try {
      const res = await ordersApi.uploadHistoryFor(id, fd);
      const done = res.claim?.outcome;
      if (done === "rejected") setView((v) => (v ? { ...v, status: "rejected" } : v));
      else if (done === "not_found") setOutcome(done);
      // No claim outcome: it closed while the file was on its way (the
      // deadline refunded it). Show how it ended.
      else void load();
      return res;
    } catch (err: any) {
      // 409: the order moved on (refunded) — re-read; the uploader shows its own message.
      if (err?.response?.status === 409) void load();
      throw err;
    }
  };

  const refund = async () => {
    if (!view || refunding) return;
    if (!window.confirm(t("claimRespondPage.refundConfirm", { amount: formatTxnAmount(view.total, view.currency) }))) return;
    setRefunding(true);
    setRefundFailed(false);
    try {
      await claimsApi.refund(id);
      setView((v) => (v ? { ...v, status: "refunded" } : v));
    } catch {
      // 409: the claim was settled meanwhile (the set list answered it, or
      // the deadline refunded it) — re-read and show how it ended.
      try {
        const fresh = await claimsApi.get(id);
        setView(fresh);
        if (fresh.status === "open") setRefundFailed(true);
      } catch {
        setRefundFailed(true);
      }
    }
    setRefunding(false);
  };

  if (!view) {
    return (
      <div className="dispute-upload-page">
        <div className="dispute-upload-card">
          <p className="dispute-upload-loading">
            {loadFailed ? (
              <T
                k="claimRespondPage.couldnTLoad"
                tags={{ 0: (c) => <button type="button" className="claim-respond-retry" onClick={() => void load()}>{c}</button> }}
              />
            ) : (
              t("claimRespondPage.loading")
            )}
          </p>
        </div>
      </div>
    );
  }

  const song = view.searchQuery || "—";
  const status = view.status;
  // Nothing left for the talent to do: settled, or with the founder because
  // the automatic refund failed ("review").
  const settled = status !== "open";
  // The line under the request: how a settled claim ended, else what the
  // last export (this visit, or one already on file) didn't show.
  const line: Line | null =
    status !== "open" ? status : outcome ?? (view.upload.uploaded && view.upload.found === false ? "not_found" : null);
  const lineText: Record<Line, string> = {
    rejected: t("claimRespondPage.covered", { song }),
    not_found: t("claimRespondPage.notFound", { song }),
    refunded: t("claimRespondPage.refunded"),
    review: t("claimRespondPage.weReLookingInto"),
  };
  const played = fmtPlayed(view.playedAt);
  const left = claimTimeLeft(view.deadlineAt);
  const deadline = view.deadlineAt ? new Date(view.deadlineAt) : null;

  return (
    <div className="dispute-upload-page claim-respond">
      <div className="dispute-upload-card">
        <div className="dispute-upload-header">
          <h1 className="dispute-upload-title">{t("claimRespondPage.title")}</h1>
          <p className="dispute-upload-subtitle">{t("claimRespondPage.subtitle")}</p>
        </div>

        <div className="dispute-upload-order">
          <div className="dispute-upload-order-row">
            <span className="dispute-upload-order-label">{t("claimRespondPage.song")}</span>
            <span className="dispute-upload-order-value">
              {song}
              {view.trackArtists?.length ? <span className="dispute-upload-order-sub"> — {view.trackArtists.join(", ")}</span> : null}
            </span>
          </div>
          <div className="dispute-upload-order-row">
            <span className="dispute-upload-order-label">{t("claimRespondPage.amount")}</span>
            <span className="dispute-upload-order-value">
              {typeof view.djNetShare === "number"
                ? t("claimRespondPage.youKeep", {
                    total: formatTxnAmount(view.total, view.currency),
                    net: formatTxnAmount(view.djNetShare, view.currency),
                  })
                : formatTxnAmount(view.total, view.currency)}
            </span>
          </div>
          <div className="dispute-upload-order-row">
            <span className="dispute-upload-order-label">{t("claimRespondPage.played")}</span>
            <span className="dispute-upload-order-value">
              {view.venueAtPlayed ? t("claimRespondPage.playedAt", { date: played, venue: view.venueAtPlayed }) : played}
            </span>
          </div>
          <div className="dispute-upload-order-row">
            <span className="dispute-upload-order-label">{t("claimRespondPage.customer")}</span>
            <span className="dispute-upload-order-value">{view.customerName || "—"}</span>
          </div>
        </div>

        {!settled && (
          <>
            <div className="dispute-upload-reassure">
              <div className="dispute-upload-reassure-title">{t("claimRespondPage.noAnswerIn3Days")}</div>
              <p className="dispute-upload-reassure-body">{t("claimRespondPage.sameAsYourOwn")}</p>
            </div>
            {left && deadline && (
              <div className="dispute-upload-deadline">
                <div className="dispute-upload-deadline-label">{t("claimRespondPage.timeRemaining")}</div>
                <div className="dispute-upload-deadline-value">{left}</div>
                <div className="dispute-upload-deadline-sub">
                  {t("claimRespondPage.refundsAutomatically", {
                    day: deadline.toLocaleDateString(formatLocale(), { weekday: "long" }),
                    time: deadline.toLocaleTimeString(formatLocale(), { hour: "numeric", minute: "2-digit" }),
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {line && (
          <div className={`claim-respond-line claim-respond-line--${LINE_TONE[line]}`} role="status">
            {lineText[line]}
          </div>
        )}

        {!settled && (
          <>
            <TrackHistoryUploader
              key={uploads}
              upload={upload}
              onUploaded={() => setUploads((n) => n + 1)}
              submitLabel={t("claimRespondPage.checkMySetHistory")}
            />
            {refundFailed && <div className="dispute-upload-error claim-respond-refund-error">{t("claimRespondPage.couldnTRefund")}</div>}
            <button type="button" className="claim-respond-refund" onClick={refund} disabled={refunding}>
              {t("claimRespondPage.refundTheFanInstead")}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ClaimRespondPage;
